import { useTranslation } from 'react-i18next';

import Download2Line from '@/assets/icons/download_2_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/molecules/Menu';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { fetchImageBlob, type Image, imageFileName, zipImages } from '@/domain/images';
import { saveBlob } from '@/utils/saveBlob';

import styles from './DownloadImages.module.scss';

const DOWNLOAD_BUTTON_NAME = 'download';

type DropdownOption = 'Single' | 'All';

const DOWNLOAD_OPTION_KEYS: Record<DropdownOption, string> = {
    Single: 'organisms.imageViewer.downloadSingle',
    All: 'organisms.imageViewer.downloadAll',
};

export interface DownloadImagesProps {
    /** Only `path` is read: the component fetches each image and zips the bytes. */
    imageList: Pick<Image, 'path'>[];
    imageIndex?: number;
}

export default function DownloadImages(props: DownloadImagesProps) {
    const { imageList, imageIndex = 0 } = props;

    const { t } = useTranslation();

    const dropdownOptions: DropdownOption[] = ['Single', 'All'];

    const handleSingleDownloadImage = async () => {
        const originalImage = imageList[imageIndex].path;
        saveBlob(await fetchImageBlob(originalImage), imageFileName(originalImage));
    };

    const handleAllDownloadImages = async () => {
        saveBlob(await zipImages(imageList.map((image) => image.path)), 'sample.zip');
    };

    const handleDownload = async (option: DropdownOption) => {
        try {
            if (option === 'Single') await handleSingleDownloadImage();
            else await handleAllDownloadImages();
        } catch {
            ToastError(t('organisms.downloadImages.failed'), 'download-images-error');
        }
    };

    return (
        <div className={styles.downloadImages}>
            <Menu>
                <Tooltip content={t('organisms.downloadImages.label')}>
                    <MenuTrigger
                        variant="toolbar"
                        size="small"
                        iconOnly
                        aria-label={t('organisms.downloadImages.label')}
                        name={DOWNLOAD_BUTTON_NAME}
                        trackingId="image_viewer_download_open"
                        icon={<SvgIcon icon={Download2Line} />}
                        caret
                    />
                </Tooltip>
                <MenuContent align="end">
                    {dropdownOptions.map((option) => (
                        <MenuItem
                            key={option}
                            trackingId={
                                option === 'Single'
                                    ? 'image_viewer_download_single'
                                    : 'image_viewer_download_all'
                            }
                            onClick={() => handleDownload(option)}>
                            {t(DOWNLOAD_OPTION_KEYS[option])}
                        </MenuItem>
                    ))}
                </MenuContent>
            </Menu>
        </div>
    );
}
